// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../config/api';
import './QuizView.css';

const QuizView = ({ selectedBooks, onClose }) => {
    const [currentStep, setCurrentStep] = useState('setup'); // 'setup', 'quiz', 'results'
    const [quizData, setQuizData] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [quizResults, setQuizResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [suggestedTopics, setSuggestedTopics] = useState([]);
    
    // État du formulaire de configuration
    const [formData, setFormData] = useState({
        topic: '',
        numQuestions: 5,
        selectedBookIds: selectedBooks.map(book => book.id)
    });

    // Charger les sujets suggérés au montage
    useEffect(() => {
        loadSuggestedTopics();
    }, []);

    const loadSuggestedTopics = async () => {
        try {
            const response = await apiFetch('quiz/topics');
            const data = await response.json();
            if (data.success) {
                setSuggestedTopics(data.topics);
            }
        } catch (error) {
            console.error('Erreur chargement sujets:', error);
        }
    };

    const generateQuiz = async () => {
        if (!formData.topic.trim()) {
            setError('Veuillez spécifier un sujet pour le quiz');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('book_ids', formData.selectedBookIds.join(','));
            formDataToSend.append('topic', formData.topic);
            formDataToSend.append('num_questions', formData.numQuestions);

            const response = await apiFetch('quiz/generate', {
                method: 'POST',
                body: formDataToSend
            });

            const data = await response.json();

            if (data.success) {
                setQuizData(data.quiz);
                setCurrentStep('quiz');
                setCurrentQuestionIndex(0);
                setUserAnswers({});
            } else {
                setError(data.detail || 'Erreur lors de la génération du quiz');
            }
        } catch (error) {
            console.error('Erreur génération quiz:', error);
            setError('Erreur de connexion lors de la génération du quiz');
        } finally {
            setLoading(false);
        }
    };

    const handleAnswerSelect = (answer) => {
        setUserAnswers(prev => ({
            ...prev,
            [currentQuestionIndex]: answer
        }));
    };

    const nextQuestion = () => {
        if (currentQuestionIndex < quizData.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const previousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const submitQuiz = async () => {
        setLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('quiz_data', JSON.stringify(quizData));
            formDataToSend.append('answers', JSON.stringify(userAnswers));

            const response = await apiFetch('quiz/submit', {
                method: 'POST',
                body: formDataToSend
            });

            const data = await response.json();

            if (data.success) {
                setQuizResults(data.results);
                setCurrentStep('results');
            } else {
                setError(data.detail || 'Erreur lors de la soumission du quiz');
            }
        } catch (error) {
            console.error('Erreur soumission quiz:', error);
            setError('Erreur de connexion lors de la soumission du quiz');
        } finally {
            setLoading(false);
        }
    };

    const restartQuiz = () => {
        setCurrentStep('setup');
        setQuizData(null);
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setQuizResults(null);
        setError(null);
    };

    const getProgressPercentage = () => {
        if (!quizData) return 0;
        return ((currentQuestionIndex + 1) / quizData.questions.length) * 100;
    };

    const getAnsweredQuestionsCount = () => {
        return Object.keys(userAnswers).length;
    };

    // Rendu de l'étape de configuration
    if (currentStep === 'setup') {
        return (
            <div className="quiz-container">
                <div className="quiz-header">
                    <h2>🧠 Générateur de Quiz Adaptatif</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="quiz-setup">
                    <div className="setup-section">
                        <h3>📚 Livres sélectionnés</h3>
                        <div className="selected-books">
                            {selectedBooks.map(book => (
                                <div key={book.id} className="book-item">
                                    <span className="book-title">{book.title}</span>
                                    <span className="book-author">{book.author}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="setup-section">
                        <h3>🎯 Sujet du quiz</h3>
                        <input
                            type="text"
                            placeholder="Ex: Histoire de France, Concepts philosophiques..."
                            value={formData.topic}
                            onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                            className="topic-input"
                        />
                        
                        {suggestedTopics.length > 0 && (
                            <div className="suggested-topics">
                                <h4>Sujets suggérés:</h4>
                                <div className="topics-grid">
                                    {suggestedTopics.slice(0, 8).map((topic, index) => (
                                        <button
                                            key={index}
                                            className="topic-suggestion"
                                            onClick={() => setFormData(prev => ({ ...prev, topic }))}
                                        >
                                            {topic}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="setup-section">
                        <h3>📝 Nombre de questions</h3>
                        <div className="question-count-selector">
                            {[3, 5, 8, 10].map(count => (
                                <button
                                    key={count}
                                    className={`count-btn ${formData.numQuestions === count ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, numQuestions: count }))}
                                >
                                    {count}
                                </button>
                            ))}
                        </div>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <div className="setup-actions">
                        <button 
                            className="generate-btn"
                            onClick={generateQuiz}
                            disabled={loading || !formData.topic.trim()}
                        >
                            {loading ? '🔄 Génération...' : '🚀 Générer le Quiz'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Rendu du quiz
    if (currentStep === 'quiz' && quizData) {
        const currentQuestion = quizData.questions[currentQuestionIndex];
        const userAnswer = userAnswers[currentQuestionIndex];

        return (
            <div className="quiz-container">
                <div className="quiz-header">
                    <h2>🧠 Quiz: {quizData.topic}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="quiz-progress">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ width: `${getProgressPercentage()}%` }}
                        ></div>
                    </div>
                    <div className="progress-text">
                        Question {currentQuestionIndex + 1} sur {quizData.questions.length}
                        <span className="answered-count">
                            ({getAnsweredQuestionsCount()} répondues)
                        </span>
                    </div>
                </div>

                <div className="question-container">
                    <div className="question-header">
                        <span className="question-number">Question {currentQuestionIndex + 1}</span>
                        <span className="book-source">Source: {currentQuestion.book_title}</span>
                    </div>

                    <div className="question-text">
                        {currentQuestion.question}
                    </div>

                    <div className="options-container">
                        {currentQuestion.options.map((option, index) => {
                            const optionLetter = String.fromCharCode(65 + index); // A, B, C, D
                            const isSelected = userAnswer === optionLetter;
                            
                            return (
                                <button
                                    key={index}
                                    className={`option-btn ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleAnswerSelect(optionLetter)}
                                >
                                    <span className="option-letter">{optionLetter}</span>
                                    <span className="option-text">{option}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="quiz-navigation">
                    <button 
                        className="nav-btn prev-btn"
                        onClick={previousQuestion}
                        disabled={currentQuestionIndex === 0}
                    >
                        ← Précédente
                    </button>

                    <div className="nav-center">
                        {currentQuestionIndex < quizData.questions.length - 1 ? (
                            <button 
                                className="nav-btn next-btn"
                                onClick={nextQuestion}
                                disabled={!userAnswer}
                            >
                                Suivante →
                            </button>
                        ) : (
                            <button 
                                className="submit-btn"
                                onClick={submitQuiz}
                                disabled={loading || getAnsweredQuestionsCount() < quizData.questions.length}
                            >
                                {loading ? '🔄 Soumission...' : '✅ Terminer le Quiz'}
                            </button>
                        )}
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}
            </div>
        );
    }

    // Rendu des résultats
    if (currentStep === 'results' && quizResults) {
        const { score, questions } = quizResults;
        const getScoreColor = (percentage) => {
            if (percentage >= 80) return 'excellent';
            if (percentage >= 60) return 'good';
            if (percentage >= 40) return 'average';
            return 'poor';
        };

        return (
            <div className="quiz-container">
                <div className="quiz-header">
                    <h2>📊 Résultats du Quiz</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="results-summary">
                    <div className={`score-display ${getScoreColor(score.percentage)}`}>
                        <div className="score-circle">
                            <span className="score-percentage">{score.percentage}%</span>
                            <span className="score-fraction">{score.correct}/{score.total}</span>
                        </div>
                        <div className="score-message">
                            {score.percentage >= 80 && "🎉 Excellent! Vous maîtrisez bien ce sujet!"}
                            {score.percentage >= 60 && score.percentage < 80 && "👍 Bien! Vous avez une bonne compréhension."}
                            {score.percentage >= 40 && score.percentage < 60 && "🤔 Moyen. Quelques révisions seraient utiles."}
                            {score.percentage < 40 && "📚 À améliorer. N'hésitez pas à relire les passages concernés."}
                        </div>
                    </div>
                </div>

                <div className="results-details">
                    <h3>Détail des réponses</h3>
                    <div className="questions-review">
                        {questions.map((question, index) => (
                            <div key={index} className={`question-review ${question.is_correct ? 'correct' : 'incorrect'}`}>
                                <div className="question-review-header">
                                    <span className="question-number">Question {index + 1}</span>
                                    <span className={`result-indicator ${question.is_correct ? 'correct' : 'incorrect'}`}>
                                        {question.is_correct ? '✅' : '❌'}
                                    </span>
                                </div>
                                
                                <div className="question-text">{question.question}</div>
                                
                                <div className="answer-details">
                                    <div className="user-answer">
                                        <strong>Votre réponse:</strong> {question.user_answer || 'Aucune'}
                                    </div>
                                    <div className="correct-answer">
                                        <strong>Réponse correcte:</strong> {question.correct_answer}
                                    </div>
                                </div>
                                
                                <div className="explanation">
                                    {question.explanation}
                                </div>
                                
                                <div className="fact-source">
                                    <strong>Source:</strong> {question.book_title}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="results-actions">
                    <button className="restart-btn" onClick={restartQuiz}>
                        🔄 Nouveau Quiz
                    </button>
                    <button className="close-btn" onClick={onClose}>
                        ✋ Fermer
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default QuizView;